import type { OrderRecord } from '@adiox/api';
import type { Position } from '../stream/accountModel';
import type { CloseAllRequest } from './tradeClient';

/** What the server is asked to do for a bulk action. */
export type BulkRequest =
  | { type: 'closeAll'; body: CloseAllRequest }
  | { type: 'closeByAll'; symbol?: string }
  | { type: 'cancelAll'; symbol?: string };

export interface BulkAction {
  id: string;
  /** The menu entry: "Close all buys (3)". */
  label: string;
  /** The question before it is done: "Close all buys (3 positions)?" */
  question: string;
  confirmLabel: string;
  count: number;
  request: BulkRequest;
}

/** One buy against one sell of a symbol, for the volume they have in common. */
export interface Pair {
  buy: number;
  sell: number;
  volume: number;
}

/** Pairs the buys and sells of each symbol oldest first, exactly as the server's Multiple Close By does. */
export function closeByPairs(positions: readonly Position[], symbol?: string): Pair[] {
  const pairs: Pair[] = [];
  const symbols = [
    ...new Set(positions.filter((p) => !symbol || p.symbol === symbol).map((p) => p.symbol)),
  ].sort();
  for (const name of symbols) {
    const left = (side: string) =>
      positions
        .filter((p) => p.symbol === name && p.side === side)
        .sort((a, b) => a.ticket - b.ticket)
        .map((p) => ({ ticket: p.ticket, volume: p.volume }));
    const buys = left('Buy');
    const sells = left('Sell');
    let i = 0;
    let j = 0;
    while (i < buys.length && j < sells.length) {
      const volume = Math.min(buys[i]!.volume, sells[j]!.volume);
      pairs.push({ buy: buys[i]!.ticket, sell: sells[j]!.ticket, volume: Number(volume.toFixed(8)) });
      buys[i]!.volume = Number((buys[i]!.volume - volume).toFixed(8));
      sells[j]!.volume = Number((sells[j]!.volume - volume).toFixed(8));
      if (buys[i]!.volume === 0) i++;
      if (sells[j]!.volume === 0) j++;
    }
  }
  return pairs;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * The bulk choices for the positions and orders on screen, counted from what is shown (the server picks again when asked, on
 * its own live figures). With `only`, just that symbol's; without, all of them and one entry per symbol.
 */
export function bulkActions(
  positions: readonly Position[],
  orders: readonly OrderRecord[],
  only?: string,
): BulkAction[] {
  const mine = only ? positions.filter((p) => p.symbol === only) : [...positions];
  const myOrders = only ? orders.filter((o) => o.symbol === only) : [...orders];
  const where = only ? ` ${only}` : '';
  const closing = (id: string, what: string, count: number, body: CloseAllRequest): BulkAction => ({
    id,
    label: `Close ${what}${where} (${count})`,
    question: `Close ${what}${where} (${plural(count, 'position')})?`,
    confirmLabel: 'Yes, close',
    count,
    request: { type: 'closeAll', body: { ...body, ...(only ? { symbol: only } : {}) } },
  });
  const profit = (p: Position) => p.profit ?? 0;
  const pairs = closeByPairs(positions, only);

  const actions: BulkAction[] = [
    closing('all', 'all', mine.length, { filter: 'All' }),
    closing('profitable', 'profitable', mine.filter((p) => profit(p) > 0).length, { filter: 'Profitable' }),
    closing('losing', 'losing', mine.filter((p) => profit(p) < 0).length, { filter: 'Losing' }),
    closing('buys', 'all buys', mine.filter((p) => p.side === 'Buy').length, { filter: 'All', side: 'Buy' }),
    closing('sells', 'all sells', mine.filter((p) => p.side === 'Sell').length, {
      filter: 'All',
      side: 'Sell',
    }),
    {
      id: 'close-by',
      label: `Close opposite pairs${only ? ` ${only}` : ''} (${pairs.length})`,
      question: `Close ${plural(pairs.length, 'buy/sell pair')}${where} against each other? The overlap of each pair is settled at the open prices, without paying the spread or an exit commission again.`,
      confirmLabel: 'Yes, close pairs',
      count: pairs.length,
      request: { type: 'closeByAll', ...(only ? { symbol: only } : {}) },
    },
    {
      id: 'cancel-orders',
      label: `Delete all pending orders${where} (${myOrders.length})`,
      question: `Delete all pending orders${where} (${plural(myOrders.length, 'order')})?`,
      confirmLabel: 'Yes, delete',
      count: myOrders.length,
      request: { type: 'cancelAll', ...(only ? { symbol: only } : {}) },
    },
  ];
  if (!only) {
    for (const symbol of [...new Set(positions.map((p) => p.symbol))].sort()) {
      const n = positions.filter((p) => p.symbol === symbol).length;
      actions.push({
        id: `symbol-${symbol}`,
        label: `Close all ${symbol} (${n})`,
        question: `Close all ${symbol} (${plural(n, 'position')})?`,
        confirmLabel: 'Yes, close',
        count: n,
        request: { type: 'closeAll', body: { filter: 'All', symbol } },
      });
    }
  }
  return actions;
}

/** The positions a given one can be closed against: the opposite side of the same symbol, oldest first. */
export const oppositesOf = (position: Position, all: readonly Position[]): Position[] =>
  all
    .filter((o) => o.symbol === position.symbol && o.side !== position.side)
    .sort((a, b) => a.ticket - b.ticket);
